import React, { useEffect, useMemo, useState } from "react";
import { Pagination } from "antd";
import styled from "styled-components";
import { AnalysisVideo } from "@/api/campaign";
import { formatNumberCommas } from "@/shared/utils/formatters";
import { CopyShortUrlButton } from "@/shared/components/Button/CopyShortUrlButton";
import { AppTooltip } from "@/shared/components/Tooltip/AppTooltip";

const TableContainer = styled.div`
	width: 100%;
	margin-top: 1rem;
	border: 1px solid #d3d3d3;
	border-radius: 8px;
	overflow: hidden;
`;

// Horizontal scroll area for the metric columns; Creator and copy-link columns stay pinned.
const TableScroll = styled.div`
	width: 100%;
	overflow-x: auto;
	/* Disable elastic overscroll so the pinned columns don't bounce at the scroll edges. */
	overscroll-behavior-x: none;
`;

const PaginationWrap = styled.div`
	padding: 6px 0px;
`;

const VideoTableStyles = styled.div`
	height: 600px;

	.video-table .ant-pagination.ant-pagination {
		display: flex !important;
		justify-content: flex-start;
		align-items: center;
		gap: 4px;
		margin: 10px 0 0 !important;
		padding-right: 4px;
		opacity: 1;
	}

	.video-table .ant-pagination-item,
	.video-table .ant-pagination-prev,
	.video-table .ant-pagination-next,
	.video-table .ant-pagination-jump-prev,
	.video-table .ant-pagination-jump-next {
		margin-inline-end: 0 !important;
		height: 40px;
		min-height: 40px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: visible;
	}

	.video-table .ant-pagination-item,
	.video-table .ant-pagination-item-active,
	.video-table .ant-pagination-jump-prev,
	.video-table .ant-pagination-jump-next,
	.video-table .ant-pagination-prev .ant-pagination-item-link,
	.video-table .ant-pagination-next .ant-pagination-item-link {
		width: 56px;
		min-width: 56px;
		height: 40px;
		padding: 8px 12px;
		border-radius: 64px;
	}

	.video-table .ant-pagination-item,
	.video-table .ant-pagination-item-active,
	.video-table .ant-pagination-prev .ant-pagination-item-link,
	.video-table .ant-pagination-next .ant-pagination-item-link,
	.video-table .ant-pagination-jump-prev,
	.video-table .ant-pagination-jump-next {
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.video-table .ant-pagination-item {
		line-height: 24px;
		border: none !important;
		background: transparent;
		opacity: 1;
		gap: 4px;
		font-size: 14px;
		font-weight: 500;
		color: #2f3138;
	}

	.video-table .ant-pagination-item a {
		color: inherit;
	}

	.video-table .ant-pagination-item:hover,
	.video-table .ant-pagination-prev:hover .ant-pagination-item-link,
	.video-table .ant-pagination-next:hover .ant-pagination-item-link,
	.video-table .ant-pagination-jump-prev:hover,
	.video-table .ant-pagination-jump-next:hover {
		background: rgba(255, 255, 255, 0.6) !important;
	}

	.video-table .ant-pagination-item:active,
	.video-table .ant-pagination-prev:active .ant-pagination-item-link,
	.video-table .ant-pagination-next:active .ant-pagination-item-link,
	.video-table .ant-pagination-jump-prev:active,
	.video-table .ant-pagination-jump-next:active {
		background: #efefef !important;
		color: #f72586 !important;
	}

	.video-table .ant-pagination-item-active {
		background: rgba(255, 255, 255, 0.6) !important;
		color: #f72586 !important;
	}

	.video-table .ant-pagination-item-active a {
		color: #f72586 !important;
	}

	.video-table .ant-pagination-prev .ant-pagination-item-link,
	.video-table .ant-pagination-next .ant-pagination-item-link {
		gap: 4px;
		border: none !important;
		background: transparent;
		opacity: 1;
		color: #2f3138;
		font-size: 16px;
		transition:
			background-color 0.18s ease,
			color 0.18s ease,
			transform 0.18s ease;
	}

	.video-table .ant-pagination-disabled .ant-pagination-item-link,
	.video-table .ant-pagination-disabled:hover .ant-pagination-item-link {
		background: transparent;
		color: #a8acb8 !important;
		transform: none;
	}
`;

const Table = styled.table`
	width: 100%;
	table-layout: fixed;
	background: white;
	border-collapse: separate;
	border-spacing: 0;
	table-layout: auto;
`;

const TableHeader = styled.thead``;

const TableHeaderCell = styled.th`
	padding: 12px 16px;
	text-align: left;
	font-weight: 600;
	color: #333;
	background: #f5f5f5;
	font-size: 14px;
	border-bottom: 2px solid #e0e0e0;
	min-width: 60px;
	/* The metric columns scroll horizontally, so labels stay on one line. */
	white-space: nowrap;

	&:first-child {
		width: 120px;
		border-top-left-radius: 8px;
		position: sticky;
		left: 0;
		z-index: 2;
		box-shadow: inset -1px 0 0 #e0e0e0;
	}

	&:last-child {
		border-top-right-radius: 8px;
		position: sticky;
		right: 0;
		z-index: 2;
		box-shadow: inset 1px 0 0 #e0e0e0;
	}
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $active?: boolean }>`
	border-bottom: 1px solid #f0f0f0;
	background: ${(props) => (props.$active ? "#EBEEFD" : "#fff")};

	&:last-child {
		border-bottom: none;
	}

	&:hover {
		background: ${(props) => (props.$active ? "#EBEEFD" : "#fafafa")};
	}
`;

const TableCell = styled.td`
	padding: 12px 16px;
	color: #666;
	font-size: 14px;
	vertical-align: middle;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	/* Pinned cells need an opaque background (inherited from the row) so scrolled cells don't show through. */
	&:first-child {
		position: sticky;
		left: 0;
		z-index: 1;
		background: inherit;
		box-shadow: inset -1px 0 0 #f0f0f0;
	}

	&:last-child {
		position: sticky;
		right: 0;
		z-index: 1;
		background: inherit;
		box-shadow: inset 1px 0 0 #f0f0f0;
	}
`;

// const ThumbnailCell = styled(TableCell)`
//   padding: 8px;
// `;

// const Thumbnail = styled.img`
//   width: 90px;
//   height: 160px;
//   object-fit: cover;
//   border-radius: 8px;
//   cursor: pointer;
//   transition: transform 0.2s;

//   &:hover {
//     transform: scale(1.05);
//   }
// `;

// const DescriptionCell = styled(TableCell)`
//   max-width: 200px;
//   overflow: hidden;
//   text-overflow: ellipsis;
//   white-space: nowrap;
// `;

const LinkCell = styled(TableCell)`
	white-space: nowrap;
	a {
		color: #1677ff;
		text-decoration: none;
		font-weight: 500;
		cursor: pointer;

		&:hover {
			text-decoration: underline;
			color: #4096ff;
		}
	}
`;

const AuthorCell = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	white-space: nowrap;
`;

const StarBadge = styled.span`
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
	line-height: 0;
`;

const parseNumericValue = (value: unknown) => {
	if (typeof value === "number") return value;
	if (typeof value !== "string") return 0;
	const parsed = Number.parseFloat(value.replace(/[^0-9.-]+/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const parseEngagementRate = (value: unknown) => {
	if (typeof value === "number") return value;
	if (typeof value !== "string") return 0;

	const cleaned = value.trim();
	const parsed = Number.parseFloat(cleaned.replace(/%$/, ""));
	if (!Number.isFinite(parsed)) return 0;
	return cleaned.includes("%") ? parsed : parsed;
};

const StarIcon = () => (
	<StarBadge aria-hidden="true">
		<svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M22.9448 10.0203L18.2099 14.0293L19.6282 19.9979C19.7033 20.31 19.6819 20.6365 19.5667 20.9368C19.4515 21.2371 19.2476 21.4978 18.9805 21.6862C18.7134 21.8747 18.395 21.9826 18.065 21.9964C17.7351 22.0103 17.4082 21.9295 17.1254 21.7641L11.7595 18.6066L6.40523 21.7641C6.12237 21.9295 5.79553 22.0103 5.46558 21.9964C5.13563 21.9826 4.8172 21.8747 4.55012 21.6862C4.28304 21.4978 4.07914 21.2371 3.96393 20.9368C3.84872 20.6365 3.82731 20.31 3.90236 19.9979L5.31862 14.0354L0.582697 10.0203C0.332209 9.81066 0.15108 9.53398 0.0620239 9.22492C-0.027032 8.91585 -0.0200504 8.58816 0.0820931 8.28293C0.184237 7.9777 0.376995 7.70852 0.636196 7.50915C0.895397 7.30977 1.2095 7.18909 1.53912 7.16221L7.7816 6.63766L10.2183 0.99899C10.3456 0.703119 10.5602 0.450389 10.8352 0.272622C11.1102 0.0948553 11.4333 0 11.7637 0C12.0942 0 12.4172 0.0948553 12.6922 0.272622C12.9672 0.450389 13.1819 0.703119 13.3091 0.99899L15.7532 6.63766L21.9936 7.16221C22.3232 7.18909 22.6373 7.30977 22.8965 7.50915C23.1557 7.70852 23.3485 7.9777 23.4506 8.28293C23.5528 8.58816 23.5597 8.91585 23.4707 9.22492C23.3816 9.53398 23.2005 9.81066 22.95 10.0203H22.9448Z"
				fill="#FFCC00"
			/>
			<path d="M9.94669 10.1809V8.64021H12.6308V15.7139H10.906V10.1809H9.94669Z" fill="#806600" />
		</svg>
	</StarBadge>
);

interface VideoTableProps {
	videos: AnalysisVideo[];
	loading?: boolean;
	isCustomizing?: boolean;
	videoVisibility?: { [key: string]: boolean };
	onVideoVisibilityToggle?: (videoId: string) => void;
	onSettingsClick?: (videoId: string) => void;
	onOrderChange?: (videos: AnalysisVideo[]) => void;
	onRowClick?: (creatorId: string) => void;
	activeCreatorId?: string;
	mode?: "edit" | "view";
}

export const VideoTable: React.FC<VideoTableProps> = ({ videos, loading = false, onRowClick, activeCreatorId }) => {
	const PAGE_SIZE = 6;
	const [currentPage, setCurrentPage] = useState(1);

	const metricColumns = [
		{ key: "viewCount", label: "Views" },
		{ key: "likes", label: "Likes" },
		{ key: "comments", label: "Comments" },
		{ key: "saves", label: "Saves" },
		{ key: "shares", label: "Shares" },
	] as const;

	const getMetricValue = (video: AnalysisVideo, key: (typeof metricColumns)[number]["key"]) => {
		if (key === "saves" && video.platform === "facebook") {
			return (
				<AppTooltip title="Coming soon" arrow={false}>
					<div style={{ width: "100%" }}>-</div>
				</AppTooltip>
			);
		}

		if ((key === "saves" || key === "shares") && video.platform === "instagram") {
			return (
				<AppTooltip title="Coming soon" arrow={false}>
					<div style={{ width: "100%" }}>-</div>
				</AppTooltip>
			);
		}

		if (key === "likes" && video.platform === "instagram") {
			const value = Number(video.likes) || 0;
			if (value <= 0) {
				return (
					<AppTooltip title="Data unavailable" arrow={false}>
						<div style={{ width: "100%" }}>-</div>
					</AppTooltip>
				);
			}
			return formatNumberCommas(value);
		}

		if ((key === "saves" || key === "shares") && video.platform === "youtube") {
			return (
				<AppTooltip title="Coming soon" arrow={false}>
					<div style={{ width: "100%" }}>-</div>
				</AppTooltip>
			);
		}

		return formatNumberCommas(Number((video as any)[key]) || 0);
	};

	const getFollowerValue = (followers: number | string | undefined) => {
		return typeof followers === "number" ? formatNumberCommas(followers) : followers || "Not part of Campaign";
	};

	const creatorViewTotals = useMemo(() => {
		const totals = new Map<string, number>();

		(videos || []).forEach((video) => {
			const creatorName = video.author?.handleName || "Unknown";
			totals.set(creatorName, (totals.get(creatorName) || 0) + parseNumericValue(video.viewCount));
		});

		return totals;
	}, [videos]);

	const sortedVideos = useMemo(() => {
		const creatorOrder = Array.from(creatorViewTotals.entries())
			.sort((a, b) => {
				if (b[1] !== a[1]) return b[1] - a[1];
				return a[0].localeCompare(b[0]);
			})
			.map(([creatorName]) => creatorName);

		const creatorRank = new Map(creatorOrder.map((creatorName, index) => [creatorName, index]));

		return [...(videos || [])].sort((a, b) => {
			const creatorA = a.author?.handleName || "Unknown";
			const creatorB = b.author?.handleName || "Unknown";
			const rankA = creatorRank.get(creatorA) ?? Number.MAX_SAFE_INTEGER;
			const rankB = creatorRank.get(creatorB) ?? Number.MAX_SAFE_INTEGER;

			if (rankA !== rankB) return rankA - rankB;

			const creatorCompare = creatorA.localeCompare(creatorB);
			if (creatorCompare !== 0) return creatorCompare;

			const viewA = parseNumericValue(a.viewCount);
			const viewB = parseNumericValue(b.viewCount);
			if (viewB !== viewA) return viewB - viewA;

			return a.id.localeCompare(b.id);
		});
	}, [videos, creatorViewTotals]);

	useEffect(() => {
		setCurrentPage(1);
	}, [videos]);

	const paginatedVideos = useMemo(() => {
		const start = (currentPage - 1) * PAGE_SIZE;
		return sortedVideos.slice(start, start + PAGE_SIZE);
	}, [currentPage, sortedVideos]);

	const totalPages = Math.ceil(sortedVideos.length / PAGE_SIZE);

	const highestViewCreatorName = useMemo(() => {
		let topCreator = "";
		let topViews = -1;

		creatorViewTotals.forEach((views, creatorName) => {
			if (views > topViews) {
				topViews = views;
				topCreator = creatorName;
			}
		});

		return topCreator;
	}, [creatorViewTotals]);

	const highestEngagementVideoId = useMemo(() => {
		let topVideoId = "";
		let topRate = -1;

		sortedVideos.forEach((video) => {
			const rate = parseEngagementRate(video.engagementRate);
			if (rate > topRate) {
				topRate = rate;
				topVideoId = video.id;
			}
		});

		return topVideoId;
	}, [sortedVideos]);

	if (loading)
		return (
			<TableContainer>
				<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>
			</TableContainer>
		);
	if (!sortedVideos.length)
		return (
			<TableContainer>
				<div style={{ padding: "2rem", textAlign: "center" }}>No videos found</div>
			</TableContainer>
		);

	let lastCreator = "";
	let videoCount = 0;

	return (
		<VideoTableStyles>
			<TableContainer>
				<TableScroll>
					<Table>
						<TableHeader>
							<tr>
								<TableHeaderCell>Creator</TableHeaderCell>
								<TableHeaderCell>Followers</TableHeaderCell>
								<TableHeaderCell>Links</TableHeaderCell>
								{metricColumns.map((column) => (
									<TableHeaderCell key={column.key}>{column.label}</TableHeaderCell>
								))}
								<TableHeaderCell>Total Engagements</TableHeaderCell>
								<TableHeaderCell>Engage Rate</TableHeaderCell>
								<TableHeaderCell></TableHeaderCell>
							</tr>
						</TableHeader>
						<TableBody>
							{paginatedVideos.map((video) => {
								const name = video.author?.handleName || "Unknown";
								const followers = video.author?.followerCount || "Not part of Campaign";
								const isFirst = name !== lastCreator;

								if (isFirst) {
									lastCreator = name;
									videoCount = 1;
								} else {
									videoCount++;
								}

								const totalEngagement =
									(Number(video.likes) || 0) +
									(Number(video.comments) || 0) +
									(Number(video.shares) || 0) +
									(Number(video.saves) || 0);
								const creatorLabel = isFirst ? name : "";
								const followerLabel = isFirst ? getFollowerValue(followers) : "";
								const isActive = activeCreatorId === name;
								const showCreatorStar = creatorLabel && name === highestViewCreatorName;
								const showEngagementStar = video.id === highestEngagementVideoId;

								return (
									<TableRow
										key={video.id}
										$active={isActive}
										onClick={() => onRowClick?.(video.author?.handleName || "Unknown")}
										style={{ cursor: onRowClick ? "pointer" : "default" }}
									>
										<TableCell>
											{creatorLabel && (
												<AuthorCell>
													<span>{creatorLabel}</span>
													{showCreatorStar && <StarIcon />}
												</AuthorCell>
											)}
										</TableCell>
										<TableCell>
											{followerLabel && <AuthorCell>{followerLabel}</AuthorCell>}
										</TableCell>
										<LinkCell>
											<a href={video.url} target="_blank" rel="noopener noreferrer">
												Video {videoCount}
											</a>
										</LinkCell>
										{metricColumns.map((column) => (
											<TableCell key={column.key}>{getMetricValue(video, column.key)}</TableCell>
										))}
										<TableCell>{formatNumberCommas(totalEngagement)}</TableCell>
										<TableCell>
											<AuthorCell>
												<span>{video.engagementRate || "0%"}</span>
												{showEngagementStar && <StarIcon />}
											</AuthorCell>
										</TableCell>
										<TableCell>
											<CopyShortUrlButton targetUrl={video.url} />
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableScroll>
			</TableContainer>
			{totalPages > 1 && (
				<PaginationWrap className="video-table">
					<Pagination
						current={currentPage}
						pageSize={PAGE_SIZE}
						total={sortedVideos.length}
						showSizeChanger={false}
						onChange={(page) => setCurrentPage(page)}
					/>
				</PaginationWrap>
			)}
		</VideoTableStyles>
	);
};
